import React, { useMemo } from 'react';

import { Box, Typography } from '@mui/material';
import { TableCDS } from '@sharedCR/coreDesignSystem/Table';
import _ from 'lodash';
import PropTypes from 'prop-types';

import {
  CHANGE_SIDE_LABELS,
  FIELD_MAPPING_UNREADABLE_MESSAGE,
  SAME_FILE_NOTE,
} from '../../FormChangeHistory.constants';
import { TemplateSideLabelsPropType } from '../../FormChangeHistory.propTypes';
import { changesetStyles } from '../../FormChangeHistory.styles';
import { FormChangeHistoryTestIds } from '../../FormChangeHistory.testIds';
import {
  getFieldMappingRows,
  getFieldMappingTitle,
  isSameFile,
} from '../../FormChangeHistory.utils';
import TemplateFileVersions from '../TemplateFileVersions';

import { getFieldMappingTableConfig } from './PdfTemplateFacts.config';

/**
 * An uploaded PDF template's two sides: the file's facts (left out when its checksum is the same)
 * and the field-mapping diff. The files open from a change (`auditId`); a compare shows facts only.
 */
const PdfTemplateFacts = ({ pdf, auditId = null, sideLabels = CHANGE_SIDE_LABELS }) => {
  const mappingRows = useMemo(() => getFieldMappingRows(pdf.fieldMapping), [pdf]);
  const tableConfig = useMemo(
    () => getFieldMappingTableConfig(sideLabels.position),
    [sideLabels.position],
  );

  const isFileChanged = !isSameFile(pdf);
  const hasMappingChanges = !_.isEmpty(mappingRows) || pdf.fieldMappingUnreadable;

  return (
    <Box data-testid={FormChangeHistoryTestIds.PDF_CHANGESET} sx={changesetStyles}>
      {isFileChanged && (
        <TemplateFileVersions isPdf auditId={auditId} files={pdf} sideLabels={sideLabels} />
      )}

      {!isFileChanged && !hasMappingChanges && (
        <Typography color="text.secondary" variant="regular14">
          {SAME_FILE_NOTE}
        </Typography>
      )}

      {pdf.fieldMappingUnreadable && (
        <Typography color="text.secondary" variant="regular14">
          {FIELD_MAPPING_UNREADABLE_MESSAGE}
        </Typography>
      )}

      {!_.isEmpty(mappingRows) && (
        <>
          <Typography variant="medium14">{getFieldMappingTitle(mappingRows.length)}</Typography>
          <TableCDS
            config={tableConfig}
            data={mappingRows}
            dataTestId={FormChangeHistoryTestIds.FIELD_MAPPING_TABLE}
          />
        </>
      )}
    </Box>
  );
};

PdfTemplateFacts.propTypes = {
  pdf: PropTypes.shape({
    before: PropTypes.object,
    after: PropTypes.object,
    fieldMapping: PropTypes.arrayOf(PropTypes.object),
    fieldMappingUnreadable: PropTypes.bool,
  }).isRequired,
  auditId: PropTypes.string,
  sideLabels: TemplateSideLabelsPropType,
};

export default PdfTemplateFacts;
